import type { CSSProperties } from "react"
import { DEFAULT_TENANT_ID as SHARED_DEFAULT_TENANT_ID } from "shared/utils/tenantContext"

import type {
  ReportLayoutWidgetDto,
  ReportLayoutWidgetType,
  ReportTemplateFieldDto,
  ReportTemplateFilterDto,
  ReportTemplateFrameDto,
  ReportTemplateFlowSectionDto,
  ReportTemplateLayoutDto,
  ReportTemplateDatasourceDto,
} from "../../api/reportTemplateService"

export type FieldFormState = {
  fieldKey: string
  label: string
  dataType: ReportTemplateFieldDto["dataType"]
  sourcePath: string
  isFilterable: boolean
  isSortable: boolean
  isGroupable: boolean
  defaultVisible: boolean
  orderIndex: number
}

export type FilterFormState = {
  fieldKey: string
  operator: string
  defaultValueJson: string
  required: boolean
  orderIndex: number
}

export const DEFAULT_TENANT_ID = SHARED_DEFAULT_TENANT_ID
export const DEFAULT_FLOW_SECTION_ID = "section-default"

export const FIELD_TYPE_OPTIONS: Array<{ value: ReportTemplateFieldDto["dataType"]; label: string }> = [
  { value: "string", label: "String" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "datetime", label: "DateTime" },
  { value: "boolean", label: "Boolean" },
  { value: "image", label: "Image" },
]

export const FILTER_OPERATOR_OPTIONS = [
  { value: "=", label: "Equals (=)" },
  { value: "contains", label: "Contains" },
  { value: "in", label: "In List" },
  { value: "between", label: "Between" },
  { value: ">", label: "Greater Than (>)" },
  { value: "<", label: "Less Than (<)" },
]

export const fieldFormInitial: FieldFormState = {
  fieldKey: "",
  label: "",
  dataType: "string",
  sourcePath: "",
  isFilterable: true,
  isSortable: true,
  isGroupable: false,
  defaultVisible: true,
  orderIndex: 1,
}

export const filterFormInitial: FilterFormState = {
  fieldKey: "",
  operator: "=",
  defaultValueJson: "",
  required: false,
  orderIndex: 1,
}

export const CANVAS_BASE_WIDTH = 900
export const CANVAS_BASE_HEIGHT = 1200
export const ACTIVE_WIDGET_Z_INDEX = 99999
export const MIN_WIDGET_WIDTH = 10
export const MIN_WIDGET_HEIGHT = 10
export const MIN_FLOW_SECTION_HEIGHT = 20
export const BORDER_SIDES = ["top", "right", "bottom", "left"] as const
export type BorderSide = typeof BORDER_SIDES[number]
export type BorderLineStyle = "solid" | "dashed"
export type HorizontalAlign = "left" | "center" | "right"
export type VerticalAlign = "top" | "center" | "bottom"
export type ResizeMode = "both" | "width" | "height"
export type FontWeightMode = "normal" | "bold"
export type FontStyleMode = "normal" | "italic"
export type ImageFitMode = "contain" | "cover" | "fill"
export type TableSourceMode = "reportRows" | "workers" | "datasource"
export type WidgetPositionMode = "flow" | "absolute"
export type LineDirection = "horizontal" | "vertical"
export const TABLE_ROW_NO_KEY = "__rowNo"
export type TableColumnWidthMap = Record<string, number>
export const DEFAULT_MANUAL_PAGE_COUNT = 1
export const MAX_MANUAL_PAGE_COUNT = 50
export const TABLE_PREVIEW_HEADER_HEIGHT = 24
export const TABLE_PREVIEW_MIN_ROW_HEIGHT = 24
export const TABLE_PREVIEW_RENDER_BUFFER = 4
export const DEFAULT_REPORT_FRAME: Required<ReportTemplateFrameDto> = {
  enabled: false,
  scope: "full",
  insetTop: 20,
  insetRight: 20,
  insetBottom: 20,
  insetLeft: 20,
  paddingTop: 0,
  paddingRight: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "#334155",
  cornerRadius: 0,
}

export const WORKER_TABLE_COLUMNS: Array<{ key: string; label: string }> = [
  { key: "fullName", label: "ชื่อผู้ปฏิบัติงาน" },
  { key: "companyName", label: "บริษัท" },
  { key: "cardNumber", label: "Card No." },
  { key: "jobRole", label: "ตำแหน่งงาน" },
  { key: "roleInPermit", label: "บทบาทในใบงาน" },
  { key: "status", label: "สถานะ" },
]

export const getCanvasMetrics = (orientation: "portrait" | "landscape") =>
  orientation === "landscape"
    ? { width: CANVAS_BASE_HEIGHT, height: CANVAS_BASE_WIDTH }
    : { width: CANVAS_BASE_WIDTH, height: CANVAS_BASE_HEIGHT }

export const WIDGET_TEMPLATES: Array<{
  type: ReportLayoutWidgetType
  label: string
  defaultWidth: number
  defaultHeight: number
  defaultProps: Record<string, unknown>
}> = [
  { type: "text", label: "Text", defaultWidth: 260, defaultHeight: 56, defaultProps: { text: "New text block" } },
  { type: "field", label: "Field", defaultWidth: 260, defaultHeight: 56, defaultProps: { label: "Field label", fieldKey: "permit_number" } },
  { type: "image", label: "Image", defaultWidth: 300, defaultHeight: 180, defaultProps: { bindingType: "static", staticUrl: "" } },
  { type: "table", label: "Table", defaultWidth: 520, defaultHeight: 220, defaultProps: { columns: "permit_number,title,status" } },
  {
    type: "line",
    label: "Line",
    defaultWidth: 260,
    defaultHeight: 48,
    defaultProps: {
      lineDirection: "horizontal",
      lineAngle: 0,
      lineThickness: 2,
      lineStyle: "solid",
      lineColor: "#334155",
      borderTop: false,
      borderRight: false,
      borderBottom: false,
      borderLeft: false,
    },
  },
  {
    type: "rectangle",
    label: "Rectangle",
    defaultWidth: 260,
    defaultHeight: 120,
    defaultProps: {
      fillColor: "transparent",
      borderTop: true,
      borderRight: true,
      borderBottom: true,
      borderLeft: true,
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: "#334155",
    },
  },
]

export const formatDateTime = (value?: string | null) => {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  return date.toLocaleString("th-TH", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export const normalizeOrder = (value: number, fallback: number) => (Number.isFinite(value) && value > 0 ? value : fallback)
export const reindexFields = (items: ReportTemplateFieldDto[]) =>
  items.map((item, index) => ({ ...item, orderIndex: index + 1 }))
export const reindexFilters = (items: ReportTemplateFilterDto[]) =>
  items.map((item, index) => ({ ...item, orderIndex: index + 1 }))

export const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))
export const createWidgetId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `widget-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

export const createDefaultLayout = (): ReportTemplateLayoutDto => ({
  pageSize: "A4",
  orientation: "portrait",
  manualPageCount: DEFAULT_MANUAL_PAGE_COUNT,
  datasources: [],
  frame: { ...DEFAULT_REPORT_FRAME },
  flowSections: [
    {
      sectionId: DEFAULT_FLOW_SECTION_ID,
      name: "Default Section",
      orderIndex: 1,
      breakMode: "flow",
      minHeight: 140,
    },
  ],
  widgets: [],
  headerSection: {
    enabled: false,
    height: 120,
    widgets: [],
  },
  footerSection: {
    enabled: false,
    height: 80,
    widgets: [],
  },
})

export const getLayoutDatasources = (layout?: ReportTemplateLayoutDto | null): ReportTemplateDatasourceDto[] =>
  Array.isArray(layout?.datasources) ? layout!.datasources! : []

export const toFiniteNumber = (value: unknown, fallback: number) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const toBoolean = (value: unknown, fallback: boolean) => {
  if (typeof value === "boolean") return value
  if (typeof value === "number") return value !== 0
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase()
    if (["true", "1", "yes", "y"].includes(normalized)) return true
    if (["false", "0", "no", "n"].includes(normalized)) return false
  }
  return fallback
}

export const getWidgetBorderConfig = (widget: ReportLayoutWidgetDto) => {
  const props = widget.props && typeof widget.props === "object" ? widget.props : {}
  const borderStyleRaw = String(props.borderStyle || "solid").toLowerCase()
  const borderStyle: BorderLineStyle = borderStyleRaw === "dashed" ? "dashed" : "solid"
  const borderWidth = clamp(Math.round(toFiniteNumber(props.borderWidth, 1)), 1, 12)
  const borderColor = String(props.borderColor || "#d1d5db")
  const sides = {
    top: toBoolean(props.borderTop, true),
    right: toBoolean(props.borderRight, true),
    bottom: toBoolean(props.borderBottom, true),
    left: toBoolean(props.borderLeft, true),
  }
  return { borderStyle, borderWidth, borderColor, sides }
}

export const buildWidgetBorderStyle = (widget: ReportLayoutWidgetDto): CSSProperties => {
  const { borderStyle, borderWidth, borderColor, sides } = getWidgetBorderConfig(widget)
  const borderValue = `${borderWidth}px ${borderStyle} ${borderColor}`
  return {
    borderTop: sides.top ? borderValue : "none",
    borderRight: sides.right ? borderValue : "none",
    borderBottom: sides.bottom ? borderValue : "none",
    borderLeft: sides.left ? borderValue : "none",
  }
}

export const getWidgetCornerRadius = (widget: ReportLayoutWidgetDto) => {
  const props = widget.props && typeof widget.props === "object" ? widget.props : {}
  return clamp(Math.round(toFiniteNumber(props.cornerRadius, 0)), 0, 80)
}

export const getWidgetTextAlign = (widget: ReportLayoutWidgetDto): HorizontalAlign => {
  const props = widget.props && typeof widget.props === "object" ? widget.props : {}
  const value = String(props.textAlign || "left").toLowerCase()
  if (value === "center" || value === "right") return value
  return "left"
}

export const getWidgetVerticalAlign = (widget: ReportLayoutWidgetDto): VerticalAlign => {
  const props = widget.props && typeof widget.props === "object" ? widget.props : {}
  const value = String(props.verticalAlign || "top").toLowerCase()
  if (value === "center" || value === "bottom") return value
  return "top"
}

export const getWidgetTypographyConfig = (widget: ReportLayoutWidgetDto) => {
  const props = widget.props && typeof widget.props === "object" ? widget.props : {}
  const fontSize = clamp(Math.round(toFiniteNumber(props.fontSize, 14)), 8, 72)
  const fontWeightRaw = String(props.fontWeight || "normal").toLowerCase()
  const fontStyleRaw = String(props.fontStyle || "normal").toLowerCase()
  const fontWeight: FontWeightMode = fontWeightRaw === "bold" ? "bold" : "normal"
  const fontStyle: FontStyleMode = fontStyleRaw === "italic" ? "italic" : "normal"
  return { fontSize, fontWeight, fontStyle }
}

export const getWidgetImageFit = (widget: ReportLayoutWidgetDto): ImageFitMode => {
  const props = widget.props && typeof widget.props === "object" ? widget.props : {}
  const value = String(props.imageFit || "contain").toLowerCase()
  if (value === "cover" || value === "fill") return value
  return "contain"
}

export const getWidgetLineConfig = (widget: ReportLayoutWidgetDto) => {
  const props = widget.props && typeof widget.props === "object" ? widget.props : {}
  const directionRaw = String(props.lineDirection || "horizontal").toLowerCase()
  const styleRaw = String(props.lineStyle || "solid").toLowerCase()
  const direction: LineDirection = directionRaw === "vertical" ? "vertical" : "horizontal"
  const lineStyle: BorderLineStyle = styleRaw === "dashed" ? "dashed" : "solid"
  const lineThickness = clamp(Math.round(toFiniteNumber(props.lineThickness, 2)), 1, 24)
  const lineColor = String(props.lineColor || "#334155")
  const rawAngle = Number(props.lineAngle)
  const lineAngle = Number.isFinite(rawAngle) ? clamp(Math.round(rawAngle), -180, 180) : (direction === "vertical" ? 90 : 0)
  return { direction, lineStyle, lineThickness, lineColor, lineAngle }
}

export const getWidgetPositionMode = (widget: ReportLayoutWidgetDto): WidgetPositionMode => {
  const props = widget.props && typeof widget.props === "object" ? widget.props : {}
  const value = String(props.positionMode || "absolute").toLowerCase()
  return value === "absolute" ? "absolute" : "flow"
}

export const getWidgetFlowOrder = (widget: ReportLayoutWidgetDto, fallback: number): number => {
  const props = widget.props && typeof widget.props === "object" ? widget.props : {}
  const value = Number(props.flowOrder)
  if (Number.isFinite(value) && value > 0) return Math.floor(value)
  return fallback
}

export const getWidgetFlowSectionId = (
  widget: ReportLayoutWidgetDto,
  fallback = DEFAULT_FLOW_SECTION_ID,
): string => {
  const props = widget.props && typeof widget.props === "object" ? widget.props : {}
  const value = String(props.flowSectionId || "").trim()
  return value || fallback
}

export const getWidgetPageBreakBefore = (widget: ReportLayoutWidgetDto): boolean => {
  const props = widget.props && typeof widget.props === "object" ? widget.props : {}
  return toBoolean(props.pageBreakBefore, false)
}

export const getWidgetManualPage = (widget: ReportLayoutWidgetDto, fallback = 1): number => {
  const props = widget.props && typeof widget.props === "object" ? widget.props : {}
  const value = Number(props.manualPage)
  if (Number.isFinite(value) && value > 0) return Math.floor(value)
  return fallback
}

export const getWidgetLocked = (widget: ReportLayoutWidgetDto): boolean => {
  const props = widget.props && typeof widget.props === "object" ? widget.props : {}
  return toBoolean(props.locked, false)
}

export const snapToGridValue = (value: number, gridSize: number, enabled: boolean): number => {
  if (!enabled) return value
  const safeGrid = clamp(Math.round(gridSize) || 8, 2, 120)
  return Math.round(value / safeGrid) * safeGrid
}

export const getLayoutManualPageCount = (layout: ReportTemplateLayoutDto): number => {
  const value = Number(layout.manualPageCount)
  if (!Number.isFinite(value)) return DEFAULT_MANUAL_PAGE_COUNT
  return clamp(Math.floor(value), DEFAULT_MANUAL_PAGE_COUNT, MAX_MANUAL_PAGE_COUNT)
}

export const getLayoutFlowSections = (layout: ReportTemplateLayoutDto): ReportTemplateFlowSectionDto[] => {
  const raw = Array.isArray(layout.flowSections) ? layout.flowSections : []
  const normalized = raw
    .map((section, index): ReportTemplateFlowSectionDto | null => {
      const sectionId = String(section?.sectionId || "").trim()
      if (!sectionId) return null
      const name = String(section?.name || `Section ${index + 1}`).trim() || `Section ${index + 1}`
      const orderIndex = clamp(Math.floor(Number(section?.orderIndex) || index + 1), 1, 9999)
      const breakMode = section?.breakMode === "newPage" ? "newPage" : "flow"
      const minHeight = clamp(Math.floor(Number(section?.minHeight) || 140), MIN_FLOW_SECTION_HEIGHT, 2400)
      return { sectionId, name, orderIndex, breakMode, minHeight }
    })
    .filter((section): section is ReportTemplateFlowSectionDto => section !== null)
    .sort((a, b) => a.orderIndex - b.orderIndex)

  if (normalized.length > 0) return normalized
  return [
    {
      sectionId: DEFAULT_FLOW_SECTION_ID,
      name: "Default Section",
      orderIndex: 1,
      breakMode: "flow",
      minHeight: 140,
    },
  ]
}

export const normalizeLayoutFrame = (
  frame: Partial<ReportTemplateFrameDto> | null | undefined,
): Required<ReportTemplateFrameDto> => {
  const source: Partial<ReportTemplateFrameDto> = frame ?? {}
  const scope = source.scope === "body" ? "body" : "full"
  return {
    enabled: toBoolean(source.enabled, DEFAULT_REPORT_FRAME.enabled),
    scope,
    insetTop: clamp(Math.round(toFiniteNumber(source.insetTop, DEFAULT_REPORT_FRAME.insetTop)), 0, 400),
    insetRight: clamp(Math.round(toFiniteNumber(source.insetRight, DEFAULT_REPORT_FRAME.insetRight)), 0, 400),
    insetBottom: clamp(Math.round(toFiniteNumber(source.insetBottom, DEFAULT_REPORT_FRAME.insetBottom)), 0, 400),
    insetLeft: clamp(Math.round(toFiniteNumber(source.insetLeft, DEFAULT_REPORT_FRAME.insetLeft)), 0, 400),
    paddingTop: clamp(Math.round(toFiniteNumber(source.paddingTop, DEFAULT_REPORT_FRAME.paddingTop)), 0, 400),
    paddingRight: clamp(Math.round(toFiniteNumber(source.paddingRight, DEFAULT_REPORT_FRAME.paddingRight)), 0, 400),
    paddingBottom: clamp(Math.round(toFiniteNumber(source.paddingBottom, DEFAULT_REPORT_FRAME.paddingBottom)), 0, 400),
    paddingLeft: clamp(Math.round(toFiniteNumber(source.paddingLeft, DEFAULT_REPORT_FRAME.paddingLeft)), 0, 400),
    borderWidth: clamp(Math.round(toFiniteNumber(source.borderWidth, DEFAULT_REPORT_FRAME.borderWidth)), 1, 12),
    borderStyle: source.borderStyle === "dashed" ? "dashed" : "solid",
    borderColor: String(source.borderColor || DEFAULT_REPORT_FRAME.borderColor),
    cornerRadius: clamp(Math.round(toFiniteNumber(source.cornerRadius, DEFAULT_REPORT_FRAME.cornerRadius)), 0, 120),
  }
}

export const normalizeLayoutForEditor = (layout?: ReportTemplateLayoutDto | null): ReportTemplateLayoutDto => {
  if (!layout) return createDefaultLayout()
  const orientation = layout.orientation ?? "portrait"
  const metrics = getCanvasMetrics(orientation)
  const usedIds = new Set<string>()
  const normalizeDatasources = (datasources: ReportTemplateDatasourceDto[] | undefined): ReportTemplateDatasourceDto[] =>
    (Array.isArray(datasources) ? datasources : []).map((datasource, index) => ({
      id: String(datasource.id || `datasource-${index + 1}`).trim() || `datasource-${index + 1}`,
      name: String(datasource.name || datasource.objectName || `Datasource ${index + 1}`).trim() || `Datasource ${index + 1}`,
      sourceType: datasource.sourceType === "view" ? "view" : "view",
      objectName: String(datasource.objectName || "").trim(),
      cardinality: datasource.cardinality === "table" ? "table" : "record",
      rowLimit: clamp(Math.round(toFiniteNumber(datasource.rowLimit, 20)), 1, 200),
      bindings: Array.isArray(datasource.bindings)
        ? datasource.bindings.map((binding, bindingIndex) => ({
            id: String(binding.id || `${datasource.id || `datasource-${index + 1}`}-binding-${bindingIndex + 1}`).trim()
              || `${datasource.id || `datasource-${index + 1}`}-binding-${bindingIndex + 1}`,
            columnName: String(binding.columnName || "").trim(),
            contextKey: String(binding.contextKey || "").trim(),
            operator: "=",
          }))
        : [],
      fields: Array.isArray(datasource.fields)
        ? datasource.fields.map((field) => ({
            fieldKey: String(field.fieldKey || "").trim(),
            label: String(field.label || field.fieldKey || "").trim(),
            dataType:
              field.dataType === "number" ||
              field.dataType === "date" ||
              field.dataType === "datetime" ||
              field.dataType === "boolean" ||
              field.dataType === "image"
                ? field.dataType
                : "string",
          }))
        : [],
    }))
  const normalizeWidgets = (widgets: ReportLayoutWidgetDto[] | undefined, seed: number) =>
    (Array.isArray(widgets) ? widgets : [])
      .map((widget, index) => {
        const baseId = String(widget.id || createWidgetId()).trim() || createWidgetId()
        let safeId = baseId
        let suffix = 1
        while (usedIds.has(safeId)) {
          safeId = `${baseId}-${suffix}`
          suffix += 1
        }
        usedIds.add(safeId)

        const safeWidth = clamp(toFiniteNumber(widget.width, 240), MIN_WIDGET_WIDTH, metrics.width)
        const safeHeight = clamp(toFiniteNumber(widget.height, 60), MIN_WIDGET_HEIGHT, metrics.height)
        const maxX = Math.max(0, metrics.width - safeWidth)
        const maxY = Math.max(0, metrics.height - safeHeight)

        return {
          id: safeId,
          type: widget.type,
          x: clamp(toFiniteNumber(widget.x, 40), 0, maxX),
          y: clamp(toFiniteNumber(widget.y, 40), 0, maxY),
          width: safeWidth,
          height: safeHeight,
          zIndex: toFiniteNumber(widget.zIndex, seed + index + 1),
          props: widget.props && typeof widget.props === "object" ? { ...widget.props } : {},
        }
      })
      .sort((a, b) => a.zIndex - b.zIndex)

  const headerSectionRaw = layout.headerSection
  const footerSectionRaw = layout.footerSection

  return {
    pageSize: layout.pageSize ?? "A4",
    orientation,
    manualPageCount: getLayoutManualPageCount(layout),
    datasources: normalizeDatasources(layout.datasources),
    frame: normalizeLayoutFrame(layout.frame),
    flowSections: getLayoutFlowSections(layout),
    widgets: normalizeWidgets(layout.widgets, 0),
    headerSection: {
      enabled: toBoolean(headerSectionRaw?.enabled, false),
      height: clamp(toFiniteNumber(headerSectionRaw?.height, 120), 60, 360),
      widgets: normalizeWidgets(headerSectionRaw?.widgets, 1000),
    },
    footerSection: {
      enabled: toBoolean(footerSectionRaw?.enabled, false),
      height: clamp(toFiniteNumber(footerSectionRaw?.height, 80), 60, 360),
      widgets: normalizeWidgets(footerSectionRaw?.widgets, 2000),
    },
  }
}

export const toWidgetTitle = (widget: ReportLayoutWidgetDto) => {
  switch (widget.type) {
    case "text":
      return String(widget.props?.text || "Text")
    case "field":
      return String(widget.props?.label || widget.props?.fieldKey || "Field")
    case "image":
      return String(widget.props?.bindingType === "field" ? `Image: ${widget.props?.fieldKey || "-"}` : "Image")
    case "table":
      return "Table"
    case "line":
      return "Line"
    case "rectangle":
      return "Rectangle"
    default:
      return widget.type
  }
}

export const resolveWidgetLabel = (widget: ReportLayoutWidgetDto, fallback: string) => {
  const props = widget.props && typeof widget.props === "object" ? widget.props : {}
  if (Object.prototype.hasOwnProperty.call(props, "label")) {
    return String(props.label ?? "")
  }
  return fallback
}

export const nextWidgetZIndex = (widgets: ReportLayoutWidgetDto[]) =>
  (widgets.reduce((max, widget) => Math.max(max, widget.zIndex || 0), 0) || 0) + 1

export const isLikelyImageUrl = (value: string) => {
  const trimmed = value.trim().toLowerCase()
  if (!trimmed) return false
  return (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("/") ||
    trimmed.startsWith("data:image/")
  )
}

export const parseTableColumnWidthMap = (value: unknown): TableColumnWidthMap => {
  const source =
    typeof value === "string"
      ? (() => {
          try {
            return JSON.parse(value) as unknown
          } catch {
            return {}
          }
        })()
      : value

  if (!source || typeof source !== "object") return {}

  const result: TableColumnWidthMap = {}
  Object.entries(source as Record<string, unknown>).forEach(([key, rawValue]) => {
    const safeKey = String(key || "").trim()
    if (!safeKey) return
    const width = Number(rawValue)
    if (!Number.isFinite(width) || width <= 0) return
    result[safeKey] = clamp(Math.round(width * 100) / 100, 1, 100)
  })
  return result
}

let measurementCanvasContext: CanvasRenderingContext2D | null | undefined

const getMeasurementContext = () => {
  if (measurementCanvasContext !== undefined) return measurementCanvasContext
  if (typeof document === "undefined") {
    measurementCanvasContext = null
    return measurementCanvasContext
  }
  const canvas = document.createElement("canvas")
  measurementCanvasContext = canvas.getContext("2d")
  return measurementCanvasContext
}

const measureWrappedTextLineCount = (
  value: unknown,
  usableWidth: number,
  fontSize = 11,
  fontWeight: "normal" | "bold" = "normal",
) => {
  const safeText = String(value ?? "-").replace(/\r/g, "")
  const maxWidth = Math.max(12, usableWidth)
  const context = getMeasurementContext()
  if (!context) {
    const avgCharWidth = fontSize * 0.56
    const charsPerLine = Math.max(1, Math.floor(maxWidth / avgCharWidth))
    return safeText
      .split("\n")
      .reduce((total, line) => total + Math.max(1, Math.ceil(Array.from(line).length / charsPerLine)), 0)
  }

  context.font = `${fontWeight} ${fontSize}px Arial`

  const countLineFragments = (line: string) => {
    if (!line) return 1
    let fragments = 1
    let currentWidth = 0
    for (const char of Array.from(line)) {
      const charWidth = context.measureText(char).width
      if (currentWidth > 0 && currentWidth + charWidth > maxWidth) {
        fragments += 1
        currentWidth = charWidth
      } else {
        currentWidth += charWidth
      }
    }
    return fragments
  }

  return safeText
    .split("\n")
    .reduce((total, line) => total + countLineFragments(line), 0)
}

export const estimateTableRowHeights = (
  widget: ReportLayoutWidgetDto,
  columns: string[],
  rows: string[][],
): number[] => {
  if (columns.length === 0 || rows.length === 0) return rows.map(() => TABLE_PREVIEW_MIN_ROW_HEIGHT)

  const widthMap = parseTableColumnWidthMap(widget.props?.columnWidths)
  const fixedColumns = columns.filter((columnKey) => Number.isFinite(widthMap[columnKey]) && widthMap[columnKey] > 0)
  const fixedTotal = fixedColumns.reduce((sum, columnKey) => sum + widthMap[columnKey], 0)
  const dynamicColumns = columns.filter((columnKey) => !fixedColumns.includes(columnKey))
  const remainingPercent = Math.max(0, 100 - fixedTotal)
  const basePercents = columns.map((columnKey) => {
    const fixed = widthMap[columnKey]
    if (Number.isFinite(fixed) && fixed > 0) return fixed
    if (dynamicColumns.length === 0) return 0
    return remainingPercent / dynamicColumns.length
  })
  const percentTotal = basePercents.reduce((sum, value) => sum + value, 0) || 100
  const columnWidths = basePercents.map((percent) => {
    const normalizedPercent = percentTotal > 0 ? percent / percentTotal : 1 / columns.length
    return Math.max(24, toFiniteNumber(widget.width, 240) * normalizedPercent)
  })

  return rows.map((rowValues) => {
    let maxLines = 1
    rowValues.forEach((cellValue, columnIndex) => {
      const usableWidth = Math.max(12, (columnWidths[columnIndex] ?? columnWidths[0] ?? 24) - 16)
      maxLines = Math.max(maxLines, measureWrappedTextLineCount(cellValue, usableWidth))
    })
    return Math.max(TABLE_PREVIEW_MIN_ROW_HEIGHT, maxLines * 14 + 8)
  })
}

export const estimateTableRenderedHeight = (
  widget: ReportLayoutWidgetDto,
  columns: string[],
  rows: string[][],
) => {
  const rowHeights = estimateTableRowHeights(widget, columns, rows)
  const bodyHeight = rowHeights.reduce((sum, value) => sum + value, 0)
  return TABLE_PREVIEW_HEADER_HEIGHT + bodyHeight + TABLE_PREVIEW_RENDER_BUFFER
}

export const estimateFieldRenderedHeight = (
  widget: ReportLayoutWidgetDto,
  label: string,
  value: string,
) => {
  const typography = getWidgetTypographyConfig(widget)
  const safeWidth = Math.max(1, toFiniteNumber(widget.width, 240))
  const safeHeight = Math.max(1, toFiniteNumber(widget.height, 56))
  const contentPadding = Math.max(0, Math.min(4, Math.floor(Math.min(safeWidth, safeHeight) / 10)))
  const usableWidth = Math.max(12, safeWidth - contentPadding * 2)
  const fontWeight = typography.fontWeight === "bold" ? "bold" : "normal"
  const lineHeight = typography.fontSize * 1.25
  const hasLabel = String(label || "").trim().length > 0
  const labelLines = hasLabel ? measureWrappedTextLineCount(label, usableWidth, typography.fontSize, fontWeight) : 0
  const valueLines = Math.max(1, measureWrappedTextLineCount(value || "-", usableWidth, typography.fontSize, fontWeight))
  const labelHeight = hasLabel ? labelLines * lineHeight + 2 : 0
  const valueHeight = valueLines * lineHeight + 2
  return Math.max(MIN_WIDGET_HEIGHT, Math.ceil(contentPadding * 2 + labelHeight + valueHeight))
}
